
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { DeviceInventoryTab } from "./DeviceInventoryTab";
import { DeviceRestrictionsTab } from "./DeviceRestrictionsTab";
import { DeviceAppsTab } from "./DeviceAppsTab";
import { DeviceCommandsTab } from "./DeviceCommandsTab";
import { DeviceNetworkRulesTab } from "./DeviceNetworkRulesTab";
import { DeviceFeaturesTab } from "./DeviceFeaturesTab";
import { DeviceBrowserTab } from "./DeviceBrowserTab";
import { DeviceRequestsTab } from "./DeviceRequestsTab";
import { type DeviceStatus } from "@/utils/deviceStatus";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";

// Use the same Device interface structure as useConvexDevices
interface DeviceDetailModalProps {
  device: any; // Use any to avoid interface conflicts for now
  onClose: () => void;
}

export const DeviceDetailModal = ({ device, onClose }: DeviceDetailModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("general");
  const isMobile = useIsMobile();

  // Restore active tab from URL on mount
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['general', 'restrictions', 'commands', 'apps', 'features', 'browser', 'requests'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Handle tab changes and update URL
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams);
  };

  const tabOptions = [
    { value: "general", label: "General", mobileLabel: "Info" },
    { value: "restrictions", label: "Restrictions", mobileLabel: "Rules" },
    { value: "commands", label: "Commands", mobileLabel: "Cmd" },
    { value: "apps", label: "Apps", mobileLabel: "Apps" },
    { value: "features", label: "Features", mobileLabel: "Features" },
    { value: "browser", label: "Browser", mobileLabel: "Web" },
    { value: "requests", label: "Requests", mobileLabel: "Req" }
  ];

  const currentTab = tabOptions.find(tab => tab.value === activeTab);

  return (
    <div className="h-full flex flex-col bg-white w-full">
      {/* Tabs - removed DeviceHeader since info is now in main PageHeader */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col h-full w-full">
        <div className="border-b">
          {/* Mobile Dropdown */}
          {isMobile ? (
            <div className="p-4">
              <Select value={activeTab} onValueChange={handleTabChange}>
                <SelectTrigger className="w-full">
                  <SelectValue>
                    {currentTab?.mobileLabel || "Select"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {tabOptions.map((tab) => (
                    <SelectItem key={tab.value} value={tab.value}>
                      {tab.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            /* Desktop Tabs */
            <TabsList className="grid w-full grid-cols-7 bg-transparent h-auto p-0 rounded-none border-0">
              {tabOptions.map((tab) => (
                <TabsTrigger 
                  key={tab.value}
                  value={tab.value} 
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 rounded-none py-3 px-6 text-sm border-0"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
        </div>

        {/* General Tab Content (formerly Inventory) */}
        <TabsContent value="general" className="flex-1 h-full m-0 w-full">
          <DeviceInventoryTab device={device} />
        </TabsContent>

        {/* Restrictions Tab Content */}
        <TabsContent value="restrictions" className="flex-1 h-full m-0 w-full">
          <DeviceRestrictionsTab device={device} />
        </TabsContent>

        {/* Commands Tab Content */}
        <TabsContent value="commands" className="flex-1 h-full m-0 w-full">
          <DeviceCommandsTab device={device} />
        </TabsContent>

        {/* Apps Tab Content with Sidebar */}
        <TabsContent value="apps" className="flex-1 h-full m-0 w-full">
          <DeviceAppsTab deviceId={device.id} />
        </TabsContent>

        {/* Features Tab Content */}
        <TabsContent value="features" className="flex-1 h-full m-0 w-full">
          <DeviceFeaturesTab device={device} />
        </TabsContent>

        {/* Browser Tab Content */}
        <TabsContent value="browser" className="flex-1 h-full m-0 w-full">
          <DeviceBrowserTab deviceId={device.id} />
        </TabsContent>

        {/* Requests Tab Content */}
        <TabsContent value="requests" className="flex-1 h-full m-0 w-full">
          <DeviceRequestsTab deviceId={device.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
